import React, { useState } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { LogOut, Monitor, Settings, Database, Server, ChevronLeft, Hexagon, TerminalSquare, Folder, Archive, Activity } from 'lucide-react';

const ControlCenterLayout = () => {
    const { logout, user } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();

    // Manage local tab state for the Tenant Detail page
    const [activeTab, setActiveTab] = useState('monitor');

    const handleLogout = () => {
        logout();
        navigate('/login');
    };

    return (
        <div className="h-screen overflow-hidden bg-slate-50 flex font-sans selection:bg-rose-100 selection:text-rose-900">
            {/* Collapsed/Mini Left Pane for Deep Focus */}
            <aside className="w-[56px] h-screen shrink-0 bg-white border-r border-slate-200 flex flex-col fixed top-0 left-0 z-50">

                {/* Branding Block */}
                <div className="h-12 border-b border-slate-200 flex items-center justify-center shrink-0 w-full hover:bg-slate-50 cursor-pointer" onClick={() => navigate('/dashboard')}>
                    <img src="/logo.png" alt="Logo" className="w-7 h-7" />
                </div>

                {/* Primary Nav (Icon Only) */}
                <nav className="flex-1 py-3 flex flex-col items-center gap-1">
                    <button
                        onClick={() => navigate(-1)}
                        className="w-9 h-9 rounded-none flex items-center justify-center text-slate-400 hover:bg-slate-100 hover:text-slate-900 transition-colors mb-3"
                        title="Retour au parc"
                    >
                        <ChevronLeft size={16} strokeWidth={3} />
                    </button>

                    <button
                        onClick={() => setActiveTab('monitor')}
                        className={`w-9 h-9 flex items-center justify-center transition-all ${activeTab === 'monitor'
                                ? 'bg-rose-50 border-l-2 border-rose-600 text-rose-600'
                                : 'text-slate-400 hover:bg-slate-50 border-l-2 border-transparent hover:border-slate-300 hover:text-slate-900'
                            }`}
                        title="Terminal Logs"
                    >
                        <Monitor size={15} />
                    </button>
                    <button
                        onClick={() => setActiveTab('metrics')}
                        className={`w-9 h-9 flex items-center justify-center transition-all ${activeTab === 'metrics'
                                ? 'bg-cyan-50 border-l-2 border-cyan-600 text-cyan-600'
                                : 'text-slate-400 hover:bg-slate-50 border-l-2 border-transparent hover:border-slate-300 hover:text-slate-900'
                            }`}
                        title="Monitoring System Live (CPU/RAM/Disk/Net)"
                    >
                        <Activity size={15} />
                    </button>
                    <button
                        onClick={() => setActiveTab('database')}
                        className={`w-9 h-9 flex items-center justify-center transition-all ${activeTab === 'database'
                                ? 'bg-slate-50 border-l-2 border-slate-900 text-slate-900'
                                : 'text-slate-400 hover:bg-slate-50 border-l-2 border-transparent hover:border-slate-300 hover:text-slate-900'
                            }`}
                        title="Stockage"
                    >
                        <Database size={15} />
                    </button>
                    <button
                        onClick={() => setActiveTab('ssh')}
                        className={`w-9 h-9 flex items-center justify-center transition-all ${activeTab === 'ssh'
                                ? 'bg-slate-50 border-l-2 border-slate-900 text-slate-900'
                                : 'text-slate-400 hover:bg-slate-50 border-l-2 border-transparent hover:border-slate-300 hover:text-slate-900'
                            }`}
                        title="SSH Terminal"
                    >
                        <TerminalSquare size={15} />
                    </button>
                    <button
                        onClick={() => setActiveTab('settings')}
                        className={`w-9 h-9 flex items-center justify-center transition-all ${activeTab === 'settings'
                                ? 'bg-slate-50 border-l-2 border-slate-900 text-slate-900'
                                : 'text-slate-400 hover:bg-slate-50 border-l-2 border-transparent hover:border-slate-300 hover:text-slate-900'
                            }`}
                        title="Configuration"
                    >
                        <Settings size={15} />
                    </button>
                    <button
                        onClick={() => setActiveTab('mongoshell')}
                        className={`w-9 h-9 flex items-center justify-center transition-all ${activeTab === 'mongoshell'
                                ? 'bg-violet-50 border-l-2 border-violet-600 text-violet-600'
                                : 'text-slate-400 hover:bg-slate-50 border-l-2 border-transparent hover:border-slate-300 hover:text-slate-900'
                            }`}
                        title="Console MongoDB"
                    >
                        <Server size={15} />
                    </button>
                    <button
                        onClick={() => setActiveTab('uploads')}
                        className={`w-9 h-9 flex items-center justify-center transition-all ${activeTab === 'uploads'
                                ? 'bg-blue-50 border-l-2 border-blue-600 text-blue-600'
                                : 'text-slate-400 hover:bg-slate-50 border-l-2 border-transparent hover:border-slate-300 hover:text-slate-900'
                            }`}
                        title="Uploads"
                    >
                        <Folder size={15} />
                    </button>
                    <button
                        onClick={() => setActiveTab('backup')}
                        className={`w-9 h-9 flex items-center justify-center transition-all ${activeTab === 'backup'
                                ? 'bg-emerald-50 border-l-2 border-emerald-600 text-emerald-600'
                                : 'text-slate-400 hover:bg-slate-50 border-l-2 border-transparent hover:border-slate-300 hover:text-slate-900'
                            }`}
                        title="Sauvegardes"
                    >
                        <Archive size={15} />
                    </button>
                </nav>

                {/* Identity / Session Block */}
                <div className="border-t border-slate-200 p-2 bg-slate-50/50 flex justify-center shrink-0">
                    <button
                        onClick={handleLogout}
                        className="w-8 h-8 flex items-center justify-center text-slate-400 hover:text-rose-600 hover:bg-white transition-colors"
                        title="Quitter la session"
                    >
                        <LogOut size={14} strokeWidth={2.5} />
                    </button>
                </div>
            </aside>

            {/* Right Work Area */}
            <main className="flex-1 flex flex-col ml-[56px] bg-slate-50 relative h-screen overflow-hidden">
                <div className="h-12 bg-white border-b border-slate-200 px-6 flex items-center justify-between shrink-0 z-40">
                    {/* Header Path */}
                    <div className="flex items-center gap-3 text-[10px] font-black uppercase tracking-[0.2em] text-slate-400">
                        <span>XORA</span>
                        <span>/</span>
                        <span className="text-rose-600">Control Center</span>
                    </div>

                    {/* Right Tools */}
                    <div className="flex items-center gap-4 text-[10px] font-black uppercase tracking-widest text-slate-500">
                        <div className="hidden md:flex items-center gap-2 px-3 py-1 bg-rose-50 border border-rose-100 rounded text-rose-600">
                            <Hexagon size={12} />
                            ISOLEMENT STRICT
                        </div>
                    </div>
                </div>

                {/* Main Content Rendered Here */}
                <div className="flex-1 flex flex-col relative min-h-0 overflow-hidden">
                    <Outlet context={{ activeTab, setActiveTab }} />
                </div>
            </main>
        </div>
    );
};

export default ControlCenterLayout;
